import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Paper, Grid } from '@mui/material';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { TEAM_STATUS_CHOICES } from 'Includes/teamStatus';
import { loadGoogleMaps } from 'Includes/googleMaps';

function parseCoordinate(value) {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
}

function ClientLocations(props) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersGroup = useRef(null);

  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status_by_team: 'all',
    coordinator_id: '',
  });

  const loadData = useCallback(() => {
    const params = { limit: 1000, pageno: 1, status: 'active' };
    if (filters.search) params.search = filters.search;
    if (filters.status_by_team !== 'all') params.status_by_team = filters.status_by_team;
    if (filters.coordinator_id) params.coordinator_id = filters.coordinator_id;

    setLoading(true);
    getRequest(GET_URL.company.api, params, props)
      .then((res) => {
        if (res && res.status === 200) {
          setCompanies(res.data?.data || []);
        }
      })
      .finally(() => setLoading(false));
  }, [filters, props]);

  useEffect(() => {
    getRequest(GET_URL.user.api, { limit: 1000, pageno: 1 }, props).then((res) => {
      if (res && res.status === 200) {
        setUsers(res.data?.data || []);
      }
    });
  }, [props]);

  useEffect(() => {
    const delay = setTimeout(() => {
      loadData();
    }, 500);
    return () => clearTimeout(delay);
  }, [loadData]);

  useEffect(() => {
    let cancelled = false
    const markers = []
    loadGoogleMaps().then((maps) => {
      if (cancelled || !mapRef.current) return
      if (!mapInstance.current) {
        mapInstance.current = new maps.Map(mapRef.current, {
          center: { lat: 20.5937, lng: 78.9629 },
          zoom: 5,
          mapTypeControl: false,
          streetViewControl: false,
        })
      }
      const map = mapInstance.current
      const bounds = new maps.LatLngBounds()
      let hasValidMarkers = false
      const info = new maps.InfoWindow()
      companies.forEach((company) => {
        const lat = parseCoordinate(company.latitude)
        const lng = parseCoordinate(company.longitude)
        if (lat === null || lng === null) return
        const position = { lat, lng }
        const marker = new maps.Marker({ map, position, title: company.name || 'Company' })
        marker.addListener('click', () => {
          info.setContent(`<strong>${company.name || 'Company'}</strong><br/>Status: ${company.status_by_team || 'N/A'}<br/>Coordinator: ${company.coordinator_name || 'Unassigned'}`)
          info.open({ map, anchor: marker })
        })
        markers.push(marker)
        bounds.extend(position)
        hasValidMarkers = true
      })
      markersGroup.current = markers
      if (hasValidMarkers) map.fitBounds(bounds, 50)
    }).catch(() => {})
    return () => {
      cancelled = true
      ;(markersGroup.current || []).forEach((marker) => marker.setMap?.(null))
      markersGroup.current = []
    }
  }, [companies]);

  useEffect(() => {
    return () => {
      mapInstance.current = null
      markersGroup.current = null
    };
  }, []);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const markerCount = companies.filter(
    (c) => parseCoordinate(c.latitude) !== null && parseCoordinate(c.longitude) !== null
  ).length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Client Locations</Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search Company"
              name="search"
              value={filters.search}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status by Team</InputLabel>
              <Select
                name="status_by_team"
                value={filters.status_by_team}
                label="Status by Team"
                onChange={handleChange}
              >
                {TEAM_STATUS_CHOICES.map(c => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Coordinator</InputLabel>
              <Select
                name="coordinator_id"
                value={filters.coordinator_id}
                label="Coordinator"
                onChange={handleChange}
              >
                <MenuItem value=""><em>All Coordinators</em></MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 1, height: '70vh', position: 'relative' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: '8px' }}></div>
        {!loading && companies.length > 0 && markerCount === 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: 16,
              bottom: 16,
              bgcolor: 'background.paper',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No map coordinates found for the selected companies. Add a map location in company billing details.
            </Typography>
          </Box>
        )}
        {!loading && companies.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: 16,
              bottom: 16,
              bgcolor: 'background.paper',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No companies match the selected filters.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ClientLocations;
